import { useEffect, useRef, useState } from 'preact/hooks';
import type { ArenaSocket } from '../realtime.js';
import type { ArenaResult, PlayerRef, PmConversation, PrivateMessage } from '../../shared/protocol.js';

/** A viewer showing the lobby feed sees every incoming private message. */
export const FEED = -1;

/**
 * Private messages shared by the lobby chat and the Messages page (decision 2026-09-29):
 * conversations with unread counts, loaded conversations, the messages of this session for the
 * lobby feed, and the unread messages found at login (announced once). A message counts as read
 * when a view shows its conversation (or the lobby feed) while the page is visible.
 */
export interface PmState {
  conversations: PmConversation[];
  messages: Map<number, PrivateMessage[]>;
  /** Private messages sent or received during this session (lobby feed). */
  feed: PrivateMessage[];
  totalUnread: number;
  /** Conversations with unread messages at login; null once dismissed or read. */
  loginUnread: PmConversation[] | null;
  dismissLoginUnread: () => void;
  /** Everyone a message can be sent to, also players who are not online. */
  directory: PlayerRef[];
  refreshDirectory: () => void;
  /** A view shows this conversation (or FEED) / stops showing anything. */
  view: (viewer: string, conversation: number | null) => void;
  /** Loads the conversation and marks it read. */
  openConversation: (withId: number) => void;
  send: (toUserId: number, text: string) => Promise<ArenaResult>;
}

export function usePrivateMessages(socket: ArenaSocket | null, userId: number): PmState {
  const [conversations, setConversations] = useState<PmConversation[]>([]);
  const [messages, setMessages] = useState<Map<number, PrivateMessage[]>>(new Map());
  const [feed, setFeed] = useState<PrivateMessage[]>([]);
  const [loginUnread, setLoginUnread] = useState<PmConversation[] | null>(null);
  const [directory, setDirectory] = useState<PlayerRef[]>([]);
  const viewers = useRef(new Map<string, number>());
  const firstLoad = useRef(true);

  const isViewing = (withId: number) =>
    document.visibilityState === 'visible' && [...viewers.current.values()].some((v) => v === withId || v === FEED);

  const markRead = (withId: number) => {
    socket?.emit('pm:read', { withUserId: withId }, () => {});
    setConversations((list) => list.map((c) => (c.with.id === withId && c.unread > 0 ? { ...c, unread: 0 } : c)));
    setLoginUnread((list) => {
      if (!list) return list;
      const rest = list.filter((c) => c.with.id !== withId);
      return rest.length > 0 ? rest : null;
    });
  };

  useEffect(() => {
    if (!socket) return;
    const load = () =>
      socket.emit('pm:conversations', (r: ArenaResult<{ conversations: PmConversation[] }>) => {
        if (!r.ok) return;
        setConversations(r.conversations);
        if (firstLoad.current) {
          firstLoad.current = false;
          const unread = r.conversations.filter((c) => c.unread > 0);
          if (unread.length > 0) setLoginUnread(unread);
        }
      });
    const onPm = (m: PrivateMessage) => {
      const mine = m.from.id === userId;
      const other = mine ? m.to : m.from;
      setFeed((list) => [...list, m].slice(-100));
      setMessages((map) => {
        const list = map.get(other.id);
        return list ? new Map(map).set(other.id, [...list, m]) : map;
      });
      const seen = !mine && isViewing(other.id);
      if (seen) socket.emit('pm:read', { withUserId: other.id }, () => {});
      setConversations((list) => {
        const existing = list.find((c) => c.with.id === other.id);
        const unread = mine ? (existing?.unread ?? 0) : seen ? 0 : (existing?.unread ?? 0) + 1;
        return [{ with: other, lastAt: m.sentAt, unread }, ...list.filter((c) => c.with.id !== other.id)];
      });
    };
    // Coming back to the page: the conversations on screen are now read.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      for (const v of viewers.current.values()) if (v !== FEED) markRead(v);
    };
    load();
    socket.on('connect', load);
    socket.on('pm:message', onPm);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      socket.off('connect', load);
      socket.off('pm:message', onPm);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [socket, userId]);

  return {
    conversations,
    messages,
    feed,
    totalUnread: conversations.reduce((sum, c) => sum + c.unread, 0),
    loginUnread,
    dismissLoginUnread: () => setLoginUnread(null),
    directory,
    refreshDirectory: () =>
      socket?.emit('pm:directory', (r: ArenaResult<{ players: PlayerRef[] }>) => {
        if (r.ok) setDirectory(r.players.filter((p) => p.id !== userId));
      }),
    view: (viewer, conversation) => {
      if (conversation === null) viewers.current.delete(viewer);
      else viewers.current.set(viewer, conversation);
    },
    openConversation: (withId) => {
      socket?.emit('pm:history', { withUserId: withId }, (r: ArenaResult<{ messages: PrivateMessage[] }>) => {
        if (r.ok) setMessages((map) => new Map(map).set(withId, r.messages));
      });
      markRead(withId);
    },
    send: (toUserId, text) =>
      new Promise((resolve) => {
        if (!socket) return resolve({ ok: false, error: 'internal_error' });
        socket.emit('pm:send', { toUserId, text }, (r: ArenaResult) => resolve(r));
      }),
  };
}
